/**
 * 워크스페이스를 가로지르는 RAID.
 * 리스크·이슈·결정/요청·가정 중 닫히지 않은 기록을 한 목록으로 모은다. 정책은 넣지 않는다.
 */
import { diffDays, isISODate, type ISODate } from "@/engine/date";
import type { ProjectRecord, RecordKind, RecordSeverity, RecordStatus } from "@/engine/types";
import { recordCode, recordPhase, riskBand, riskScore, SEVERITY_LABEL } from "@/store/record-ops";

export type RaidBucket = "risk" | "issue" | "decision" | "assumption";

export const RAID_BUCKETS: RaidBucket[] = ["risk", "issue", "decision", "assumption"];
export const RAID_BUCKET_LABEL: Record<RaidBucket, string> = {
  risk: "리스크",
  issue: "이슈",
  decision: "결정·요청",
  assumption: "가정",
};

export type RaidAge = "all" | "week" | "fortnight" | "month" | "older";
export type RaidScoreBand = "all" | "critical" | "high" | "medium" | "low" | "none";

export const RAID_AGE_LABEL: Record<RaidAge, string> = {
  all: "전체",
  week: "7일 이내",
  fortnight: "8–14일",
  month: "15–28일",
  older: "29일 이상",
};

export const RAID_SCORE_LABEL: Record<RaidScoreBand, string> = {
  all: "전체",
  critical: "매우 높음",
  high: "높음",
  medium: "보통",
  low: "낮음",
  none: "점수 없음",
};

export interface RaidSource {
  workspaceId: string;
  workspaceName: string;
  projectId: string;
  projectName: string;
  records: readonly ProjectRecord[];
}

export interface RaidRow {
  key: string;
  workspaceId: string;
  workspaceName: string;
  projectId: string;
  projectName: string;
  recordId: string;
  kind: RecordKind;
  bucket: RaidBucket;
  code: string;
  title: string;
  owners: string[];
  ownerLabel: string;
  severity: RecordSeverity | null;
  score: number | null;
  /** 점수 또는 심각도. 정렬·필터가 같이 쓴다. */
  band: Exclude<RaidScoreBand, "all">;
  ageDays: number;
  overdue: boolean;
  due?: string;
  status: RecordStatus;
}

export function raidBucket(kind: RecordKind): RaidBucket | null {
  if (kind === "risk") return "risk";
  if (kind === "issue") return "issue";
  if (kind === "decision" || kind === "change") return "decision";
  if (kind === "assumption") return "assumption";
  return null;
}

function openedOn(record: ProjectRecord): ISODate | null {
  const day = record.createdAt.slice(0, 10);
  return isISODate(day) ? day : null;
}

function bandOf(record: ProjectRecord, score: number | null): Exclude<RaidScoreBand, "all"> {
  if (score != null) {
    const band = riskBand(score);
    return band;
  }
  if (record.severity === "high") return "high";
  if (record.severity === "medium") return "medium";
  if (record.severity === "low") return "low";
  return "none";
}

export function ageBandOf(days: number): Exclude<RaidAge, "all"> {
  if (days <= 7) return "week";
  if (days <= 14) return "fortnight";
  if (days <= 28) return "month";
  return "older";
}

export function collectRaid(sources: readonly RaidSource[], asOf: ISODate, includeClosed = false): RaidRow[] {
  const rows: RaidRow[] = [];
  for (const source of sources) {
    for (const record of source.records) {
      const bucket = raidBucket(record.kind);
      if (!bucket) continue;
      if (!includeClosed && recordPhase(record.status) === "closed") continue;
      const opened = openedOn(record);
      const ageDays = opened && opened <= asOf ? diffDays(opened, asOf) : 0;
      const score = record.kind === "risk" ? riskScore(record) : null;
      const owners = record.assignees.map((name) => name.trim()).filter(Boolean);
      const overdue = !!record.due && record.due < asOf && recordPhase(record.status) !== "closed";
      rows.push({
        key: `${source.workspaceId}:${source.projectId}:${record.id}`,
        workspaceId: source.workspaceId,
        workspaceName: source.workspaceName,
        projectId: source.projectId,
        projectName: source.projectName,
        recordId: record.id,
        kind: record.kind,
        bucket,
        code: recordCode(record),
        title: record.title.trim() || "제목 없음",
        owners,
        ownerLabel: owners.join(", ") || "—",
        severity: record.severity ?? null,
        score,
        band: bandOf(record, score),
        ageDays,
        overdue,
        ...(record.due ? { due: record.due } : {}),
        status: record.status,
      });
    }
  }
  return rows;
}

const SEVERITY_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, none: 0 };

/** 점수 높은 순. 점수가 없으면 뒤로, 그다음은 심각도·나이. */
export function sortRaid(rows: readonly RaidRow[]): RaidRow[] {
  return [...rows].sort((a, b) => {
    const as = a.score ?? -1;
    const bs = b.score ?? -1;
    if (as !== bs) return bs - as;
    const ar = SEVERITY_RANK[a.band] ?? 0;
    const br = SEVERITY_RANK[b.band] ?? 0;
    if (ar !== br) return br - ar;
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.ageDays !== b.ageDays) return b.ageDays - a.ageDays;
    return a.code.localeCompare(b.code, "ko");
  });
}

export interface RaidFilter {
  query?: string;
  bucket?: RaidBucket | "all";
  workspaceId?: string | "all";
  projectId?: string | "all";
  owner?: string | "all";
  score?: RaidScoreBand;
  age?: RaidAge;
  overdueOnly?: boolean;
}

export function filterRaid(rows: readonly RaidRow[], filter: RaidFilter): RaidRow[] {
  const query = filter.query?.trim().toLowerCase() ?? "";
  return rows.filter((row) => {
    if (filter.bucket && filter.bucket !== "all" && row.bucket !== filter.bucket) return false;
    if (filter.workspaceId && filter.workspaceId !== "all" && row.workspaceId !== filter.workspaceId) return false;
    if (filter.projectId && filter.projectId !== "all" && row.projectId !== filter.projectId) return false;
    if (filter.owner && filter.owner !== "all" && !row.owners.includes(filter.owner)) return false;
    if (filter.score && filter.score !== "all" && row.band !== filter.score) return false;
    if (filter.age && filter.age !== "all" && ageBandOf(row.ageDays) !== filter.age) return false;
    if (filter.overdueOnly && !row.overdue) return false;
    if (!query) return true;
    const hay = `${row.code} ${row.title} ${row.projectName} ${row.workspaceName} ${row.ownerLabel}`.toLowerCase();
    return hay.includes(query);
  });
}

export function raidScoreLabel(row: RaidRow): string {
  if (row.score != null) return String(row.score);
  if (row.severity) return SEVERITY_LABEL[row.severity];
  return "—";
}
